import { useEffect, useRef, useState } from 'react';
import {
  Alert as MuiAlert,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import ConfirmDialog from '@/components/modals/ConfirmDialog';
import PageHeader from '@/content/Management/CustomerInvoiceProfiles/PageHeader';
import CustomerInvoiceProfilesTable from '@/content/Management/CustomerInvoiceProfiles/CustomerInvoiceProfilesTable';
import CustomerInvoiceProfileDialog from '@/content/Management/CustomerInvoiceProfiles/CustomerInvoiceProfileDialog';
import { customerInvoiceProfileService } from '@/data/customerInvoiceProfileService';
import { companyService } from '@/data/companyService';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { Company } from '@/types/Company.interface';
import {
  CustomerInvoiceProfile,
  UpdateCustomerInvoiceProfileDto
} from '@/types/CustomerInvoiceProfile.interface';
import { Filters } from '@/types/Filters';

/**
 * Kayıtlı Alıcılar: the buyers registered at the POS for e-Belge sales.
 * Company scoped. Buyers are created at the POS, so this page only lists,
 * edits and removes them. SUPER_ADMIN has no company of their own and must pick
 * one (the backend requires `companyId` for them).
 */
const CustomerInvoiceProfilesManagement = () => {
  const { t } = useTranslation();
  const { isSuperAdmin } = useUserViewMode();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );

  const [profiles, setProfiles] = useState<CustomerInvoiceProfile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastFilters, setLastFilters] = useState<Filters | undefined>();

  const [editingProfile, setEditingProfile] =
    useState<CustomerInvoiceProfile | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<CustomerInvoiceProfile | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!isSuperAdmin) return;
    companyService
      .getAllFlat({ getAll: true, status: 'ACTIVE' })
      .then((data: any) => {
        const list: Company[] = Array.isArray(data) ? data : data?.items || [];
        setCompanies(list);
      })
      .catch(() => setCompanies([]));
  }, [isSuperAdmin]);

  // Only the answer of the latest request may touch the state: fast search
  // typing or a company switch can make requests finish out of order.
  const listRequestId = useRef(0);

  const fetchProfiles = async (filters?: Filters) => {
    setLastFilters(filters);
    const requestId = ++listRequestId.current;
    if (isSuperAdmin && !selectedCompanyId) {
      setProfiles([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // 0 based page, as the backend expects; `search` maps to `q`.
      const response = await customerInvoiceProfileService.list({
        q: filters?.search || undefined,
        page: filters?.page ?? 0,
        limit: filters?.limit ?? 10,
        ...(isSuperAdmin && selectedCompanyId
          ? { companyId: selectedCompanyId }
          : {})
      });
      if (requestId !== listRequestId.current) return;
      setProfiles(response.items ?? []);
      setTotalCount(response.total ?? 0);
    } catch (err: any) {
      if (requestId !== listRequestId.current) return;
      setError(
        err?.response?.data?.message || t('customerInvoiceProfiles.error.fetch')
      );
    } finally {
      if (requestId === listRequestId.current) setLoading(false);
    }
  };

  const handleSave = async (dto: UpdateCustomerInvoiceProfileDto) => {
    if (!editingProfile) return;
    try {
      setSaving(true);
      await customerInvoiceProfileService.update(editingProfile.id, dto);
      setSuccessMessage(t('customerInvoiceProfiles.update.success'));
      setEditingProfile(null);
    } catch (err: any) {
      // Keep the dialog open so the user can correct the data.
      setError(
        err?.response?.data?.message || t('customerInvoiceProfiles.error.save')
      );
      return;
    } finally {
      setSaving(false);
    }
    await fetchProfiles(lastFilters);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await customerInvoiceProfileService.delete(target.id);
      setSuccessMessage(t('customerInvoiceProfiles.delete.success'));
    } catch (err: any) {
      // The backend refuses (400, Turkish message) while receivables are open.
      setError(
        err?.response?.data?.message ||
          t('customerInvoiceProfiles.error.delete')
      );
      return;
    }
    await fetchProfiles(lastFilters);
  };

  const showTable = !isSuperAdmin || selectedCompanyId !== null;

  return (
    <>
      <Helmet>
        <title>{t('customerInvoiceProfiles.title')}</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader />
      </PageTitleWrapper>

      <Grid container spacing={3}>
        {isSuperAdmin && (
          <Grid item xs={12} sx={{ maxWidth: '95%', mx: 'auto' }}>
            <Paper sx={{ p: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="customer-invoice-profiles-company">
                  {t('company')}
                </InputLabel>
                <Select
                  labelId="customer-invoice-profiles-company"
                  label={t('company')}
                  value={selectedCompanyId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedCompanyId(value === '' ? null : Number(value));
                  }}
                >
                  <MenuItem value="">
                    <em>{t('customerInvoiceProfiles.selectCompany')}</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>
        )}

        {showTable ? (
          <Grid item xs={12}>
            <CustomerInvoiceProfilesTable
              key={selectedCompanyId ?? 'own-company'}
              profiles={profiles}
              loading={loading}
              totalCount={totalCount}
              onFilterChange={(filters) => void fetchProfiles(filters)}
              onEditProfile={setEditingProfile}
              onDeleteProfile={setDeleteTarget}
              pageKey={isSuperAdmin ? undefined : 'customer-invoice-profiles'}
            />
          </Grid>
        ) : (
          <Grid item xs={12} sx={{ maxWidth: '95%', mx: 'auto' }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {t('customerInvoiceProfiles.selectCompany.hint')}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      <CustomerInvoiceProfileDialog
        open={Boolean(editingProfile)}
        profile={editingProfile}
        saving={saving}
        onClose={() => setEditingProfile(null)}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirmed}
        title={t('confirm.delete')}
        message={t('customerInvoiceProfiles.delete.confirm', {
          name: deleteTarget?.name
        })}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(undefined)}
      >
        <MuiAlert
          variant="filled"
          severity="success"
          onClose={() => setSuccessMessage(undefined)}
        >
          <Typography>{successMessage}</Typography>
        </MuiAlert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={() => setError(undefined)}
      >
        <MuiAlert
          variant="filled"
          severity="error"
          onClose={() => setError(undefined)}
        >
          <Typography>{error}</Typography>
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default CustomerInvoiceProfilesManagement;
